<template>
  <div class="tpd-content-layout">
    <div class="tpd-content-box pb-30">
      <!-- Header -->
      <div class="faqs-header mb-40">
        <h3 class="faqs-main-title mb-2">
          <i class="fa-solid fa-circle-question me-2 text-primary"></i>
          {{ lms_website.__('Frequently Asked Questions') }}
        </h3>
        <p class="faqs-subtitle mb-0">
          {{ lms_website.__('Find answers to the most common questions below.') }}
        </p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="faqs-loading text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-3 text-muted">{{ lms_website.__('Loading FAQs...') }}</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="categories.length === 0" class="faqs-empty text-center py-5">
        <div class="faqs-empty-icon mb-3">
          <i class="fa-solid fa-folder-open fa-3x text-muted"></i>
        </div>
        <h5 class="text-muted">{{ lms_website.__('No FAQs available at the moment.') }}</h5>
        <p class="text-muted">{{ lms_website.__('Please check back later.') }}</p>
      </div>

      <!-- All FAQ Sections -->
      <div v-else class="faqs-all-sections">
        <div
          v-for="(category, index) in categories"
          :key="index"
          class="faqs-section"
          :class="{ 'mb-30': index < categories.length - 1 }"
        >
          <!-- Section Header -->
          <div class="faqs-section-header mb-15">
            <div class="d-flex align-items-center gap-3">
              <div class="faqs-section-icon">
                <i :class="category.icon"></i>
              </div>
              <div>
                <h5 class="faqs-section-title mb-0">{{ category.label }}</h5>
              </div>
            </div>
          </div>

          <!-- Section Content Card -->
          <div class="faqs-card">
            <div class="faqs-card-body" v-html="category.content"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "FQAs",
  setup() {
    return {
      lms_website,
    };
  },
  data() {
    return {
      loading: true,
      faqData: null,
    };
  },
  computed: {
    categories() {
      if (!this.faqData) return [];
      return [
        {
          key: "payment_policy",
          label: this.lms_website.__("Payment Policy"),
          icon: "fa-solid fa-credit-card",
          content: this.faqData.payment_policy || "",
        },
        {
          key: "certificates",
          label: this.lms_website.__("Certificates"),
          icon: "fa-solid fa-certificate",
          content: this.faqData.certificates || "",
        },
        {
          key: "attendence",
          label: this.lms_website.__("Attendance"),
          icon: "fa-solid fa-clipboard-check",
          content: this.faqData.attendence || "",
        },
        {
          key: "platform",
          label: this.lms_website.__("Platform"),
          icon: "fa-solid fa-desktop",
          content: this.faqData.platform || "",
        },
        {
          key: "reschedule_rules",
          label: this.lms_website.__("Reschedule Rules"),
          icon: "fa-solid fa-calendar-alt",
          content: this.faqData.reschedule_rules || "",
        },
      ].filter((cat) => cat.content && cat.content.trim() !== "");
    },
  },
  methods: {
    async fetchFAQs() {
      this.loading = true;
      try {
        frappe.call({
          type: "GET",
          method: "lms_website.api.user.get_faqs",
          callback: ({ message }) => {
            console.log(message);
            if (message) {
              this.faqData = message;
            }
            this.loading = false;
          },
        });
      } catch (error) {
        console.error("Error fetching FAQs:", error);
        this.loading = false;
        frappe.msgprint({
          title: this.lms_website.__("Error"),
          message: this.lms_website.__(
            "Unable to load FAQs. Please try again later."
          ),
          indicator: "red",
        });
      }
    },
  },
  mounted() {
    this.fetchFAQs();
    setTimeout(() => {
      NProgress.done();
    }, 300);
  },
};
</script>

<style scoped>
/* ========== Header ========== */
.faqs-main-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a2e;
  line-height: 1.3;
}

.faqs-subtitle {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
}

/* ========== Section Header ========== */
.faqs-section-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, #eff6ff, #dbeafe);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #3b82f6;
  flex-shrink: 0;
}

.faqs-section-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a2e;
}

/* ========== FAQ Card ========== */
.faqs-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.faqs-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

.faqs-card-body {
  padding: 24px 28px;
  font-size: 14px;
  line-height: 1.8;
  color: #374151;
}

.faqs-card-body :deep(p) {
  margin-bottom: 12px;
}

.faqs-card-body :deep(ul),
.faqs-card-body :deep(ol) {
  padding-left: 20px;
  margin-bottom: 12px;
}

.faqs-card-body :deep(li) {
  margin-bottom: 6px;
}

.faqs-card-body :deep(h1),
.faqs-card-body :deep(h2),
.faqs-card-body :deep(h3),
.faqs-card-body :deep(h4),
.faqs-card-body :deep(h5) {
  color: #1a1a2e;
  margin-bottom: 12px;
  font-weight: 600;
}

.faqs-card-body :deep(a) {
  color: #3b82f6;
  text-decoration: none;
}

.faqs-card-body :deep(a:hover) {
  text-decoration: underline;
}

/* ========== Loading ========== */
.faqs-loading .spinner-border {
  width: 2.5rem;
  height: 2.5rem;
}

/* ========== Empty State ========== */
.faqs-empty-icon {
  opacity: 0.5;
}

/* ========== Responsive ========== */
@media (max-width: 768px) {
  .faqs-main-title {
    font-size: 20px;
  }

  .faqs-card-body {
    padding: 18px 20px;
  }

  .faqs-section-icon {
    width: 40px;
    height: 40px;
    font-size: 16px;
    border-radius: 10px;
  }

  .faqs-section-title {
    font-size: 16px;
  }
}
</style>